import { Avatar, Card, CardContent, Divider, Drawer, Grid, Tooltip, Typography, IconButton } from '@material-ui/core';
import { Person, Lock } from '@material-ui/icons';
import { perfilDescriptions } from 'common/genericDescriptions';
import { perfilOptions } from 'common/genericOptions';
import { useGenericStyles } from 'common/genericStyles';
import { Perfil, ConviteRequest, UsuarioParticipanteApiResponse, UsuarioModelResponse } from 'common/genericTypes';
import { Button, Dropdown, LgSpace, Section, SmSpace, TextField } from 'm2m.components';
import React, { useCallback, useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from 'store';
import { validaEmail } from 'utils/validation';
import { addAlert } from 'store/modules/snackBar';

type UsuarioGroup = {
  [key in Perfil]?: UsuarioParticipanteApiResponse[];
};

export const Usuarios: React.FC = () => {
  const dispatch = useDispatch();
  const isUsuarioLeitura = useSelector((state: RootState) => state.auth.usuario.participante.perfil === Perfil.Leitura);
  const getUsuariosApi = useSelector((state: RootState) => state.usuarioApi.getUsuarios);
  const postConvite = useSelector((state: RootState) => state.usuarioApi.postConvite);
  const patchDesbloquearUsuario = useSelector((state: RootState) => state.usuarioApi.patchDesbloquearUsuario);
  const [openCadastrarUsuarioDrawer, setOpenCadastrarUsuarioDrawer] = useState(false);
  const [usuarios, setUsuarios] = useState<UsuarioParticipanteApiResponse[]>([]);
  const genericClasses = useGenericStyles();
  const [nome, setNome] = useState<string>('');
  const [cpf, setCpf] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [emailValido, setEmailValido] = useState(false);
  const [perfil, setPerfil] = useState<Perfil>();

  const getUsuarios = useCallback(async () => {
    try {
      const result = await getUsuariosApi(dispatch, true);
      setUsuarios(result);
    } catch (error) {}
  }, []);

  useEffect(() => {
    getUsuarios();
  }, []);

  const groupedUsuarios =
    usuarios.reduce<UsuarioGroup>((acc, curr) => {
      if (acc[curr.perfil]) {
        acc[curr.perfil]?.push(curr);
      } else {
        acc[curr.perfil] = [curr];
      }
      return acc;
    }, {}) ?? {};

  const handleCloseCadastrarUsuarioDrawer = () => {
    setNome('');
    setCpf('');
    setEmail('');
    setPerfil(undefined);
    setOpenCadastrarUsuarioDrawer(false);
  };

  const isNovoUsuarioValido = nome && email && emailValido && perfil;

  const handleCadastrarUsuarioClick = async () => {
    try {
      if (isNovoUsuarioValido) {
        const request: ConviteRequest = {
          nome,
          email,
          cpfCnpj: cpf,
          perfil: perfil!,
        };
        await postConvite(dispatch, request, true);
        dispatch(
          addAlert({
            message: 'O convite foi enviado. Em breve um novo usuário deve aparecer na listagem.',
            severity: 'success',
          })
        );
        handleCloseCadastrarUsuarioDrawer();
      }
    } catch (error) {}
  };

  const handleDesbloquearUsuario = async (usuario: UsuarioParticipanteApiResponse) => {
    try {
      await patchDesbloquearUsuario(dispatch, usuario.id, true);
      dispatch(
        addAlert({
          message: 'Usuário desbloqueado com sucesso!',
          severity: 'success',
        })
      );
      await getUsuarios();
    } catch (error) {
      dispatch(
        addAlert({
          message: 'Ocorreu um erro ao desbloquear o usuário!',
          severity: 'error',
        })
      );
    }
  };

  useEffect(() => setEmailValido(!!email && validaEmail(email)), [email, setEmailValido, handleDesbloquearUsuario]);

  return (
    <div style={{ marginRight: 40 }}>
      <Grid container justify={'space-between'} direction={'row'} alignItems={'center'}>
        <Typography variant="h1">{'Usuários'}</Typography>
        {!isUsuarioLeitura && (
          <Button variant="contained" color="primary" onClick={() => setOpenCadastrarUsuarioDrawer(true)}>
            {'Novo Usuário'}
          </Button>
        )}
      </Grid>
      <LgSpace />
      {Object.keys(groupedUsuarios)?.map((perfil) => (
        <Section title={perfilDescriptions[perfil as Perfil]} key={perfil}>
          <Grid container spacing={2} style={{ width: '100%' }}>
            {perfil &&
              groupedUsuarios[perfil as Perfil]?.map((usuario) => (
                <Grid key={usuario.nome} item xs={12} md={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Grid container alignItems="center" spacing={2}>
                        <Grid item>
                          <Avatar>
                            <Person />
                          </Avatar>
                        </Grid>
                        <Grid item container xs style={{ overflow: 'hidden' }}>
                          <Grid
                            item
                            container
                            xs={12}
                            style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}
                          >
                            <Typography variant="body1">{usuario.nome}</Typography>
                            {usuario.suspenso && (
                              <IconButton
                                onClick={async () => {
                                  await handleDesbloquearUsuario(usuario);
                                }}
                                style={{ height: '20px' }}
                              >
                                <Lock />
                              </IconButton>
                            )}
                          </Grid>
                          <Grid item xs={12}>
                            <Tooltip title={usuario.email} aria-label={usuario.email}>
                              <Typography variant="body1" color="textSecondary" className={genericClasses.wrapTxt}>
                                {usuario.email}
                              </Typography>
                            </Tooltip>
                          </Grid>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
          </Grid>
        </Section>
      ))}
      <Drawer
        anchor="right"
        open={openCadastrarUsuarioDrawer}
        onClose={handleCloseCadastrarUsuarioDrawer}
        PaperProps={{ style: { padding: '40px', width: 520 } }}
      >
        <Grid container>
          <Grid item md={12}>
            <Typography variant="h3">{'Novo Usuário'}</Typography>
            <SmSpace />
            <Divider variant="fullWidth" />
            <SmSpace />
            <TextField label="Nome" required value={nome} onChange={setNome} />
            <SmSpace />
            <TextField
              label="E-mail *"
              value={email}
              onChange={setEmail}
              error={!!email && !emailValido}
              errorMessage={'E-mail inválido'}
            />
            <SmSpace />
            <Dropdown
              label="Perfil"
              required
              options={perfilOptions}
              value={perfil ?? ''}
              onChange={(value) => setPerfil(value as Perfil)}
            />
            <SmSpace />
            <Button
              onClick={handleCadastrarUsuarioClick}
              variant={'contained'}
              color={'primary'}
              fullWidth
              disabled={!isNovoUsuarioValido}
            >
              {'Adicionar Usuário'}
            </Button>
          </Grid>
        </Grid>
      </Drawer>
    </div>
  );
};

export default Usuarios;
